import { isAxiosError } from 'axios';
import { Result, err, ok } from 'neverthrow';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore utils is in the parent dir
import axios from '@utils/axios';

import { TMSLocation } from 'types/Load';
import { ApiError } from 'types/api/ApiError';
import captureException from 'utils/captureException';

export type GetLocationByIDResponse = {
  location: TMSLocation;
  message: string;
};

/**
 * Fetches a single location by TMS-specific ID (e.g. 3G REST numeric loc id).
 * Returns location with default contact from TMS. Use when the user selects a
 * location and we need TMS source-of-truth contact (e.g. on location switch).
 * Returns 501 when the TMS does not support this (e.g. non-3G).
 */
export async function getLocationByID(
  tmsID: number,
  locID: string
): Promise<Result<GetLocationByIDResponse, ApiError>> {
  try {
    const params = new URLSearchParams({ tmsID: tmsID.toString() });
    const response = await axios.get<GetLocationByIDResponse>(
      `/locations/${encodeURIComponent(locID)}?${params.toString()}`
    );
    return ok(response.data);
  } catch (error) {
    captureException(error, { functionName: 'getLocationByID' });
    if (!isAxiosError(error)) {
      return err({ message: 'Failed to get location by ID' });
    }
    if (error.response?.status === 501) {
      return err({
        message:
          (error.response?.data as GetLocationByIDResponse)?.message ||
          'Location by ID is not supported for this TMS',
      });
    }
    return err({
      message:
        (error.response?.data as GetLocationByIDResponse)?.message ||
        'Failed to get location by ID',
    });
  }
}
